import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Package, ArrowLeft, RefreshCw, Plus, Search, AlertTriangle, CheckCircle,
  Boxes, DollarSign, TrendingDown, TrendingUp, Truck, MapPin, Filter,
  MoreHorizontal, Edit, History, AlertCircle, XCircle, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const STOCK_STATUS_COLORS = {
  ok: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  low: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  out_of_stock: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  overstock: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  not_tracking: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
};

const STOCK_STATUS_LABELS = {
  ok: "In Stock",
  low: "Low Stock",
  out_of_stock: "Out of Stock",
  overstock: "Overstock",
  not_tracking: "Not Tracking"
};

const CHART_COLORS = ["hsl(var(--ff-primary))", "hsl(var(--ff-income))", "hsl(var(--ff-expense))", "hsl(var(--ff-warning))", "#8884d8"];

export default function InventoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [valuation, setValuation] = useState(null);
  const [turnover, setTurnover] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Dialog states
  const [adjustmentDialog, setAdjustmentDialog] = useState({ open: false, product: null });
  const [productSettingsDialog, setProductSettingsDialog] = useState({ open: false, product: null });
  const [supplierDialog, setSupplierDialog] = useState({ open: false, supplier: null });
  const [transactionHistoryDialog, setTransactionHistoryDialog] = useState({ open: false, product: null, transactions: [] });
  
  // Form states
  const [adjustmentForm, setAdjustmentForm] = useState({
    adjustment_quantity: 0,
    adjustment_type: "adjustment",
    notes: "",
    unit_cost: ""
  });
  
  const [productSettingsForm, setProductSettingsForm] = useState({
    track_inventory: false,
    stock_quantity: 0,
    reorder_point: 0,
    reorder_quantity: 0,
    max_stock: "",
    location: "",
    supplier_id: "",
    average_cost: ""
  });
  
  const [supplierForm, setSupplierForm] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    lead_time_days: 7,
    payment_terms: "",
    notes: ""
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [overviewRes, productsRes, suppliersRes, alertsRes] = await Promise.all([
        api.get("/inventory/overview"),
        api.get("/inventory/products"),
        api.get("/suppliers"),
        api.get("/inventory/alerts?resolved=false")
      ]);
      
      setOverview(overviewRes.data);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error("Error fetching inventory data:", error);
      if (error.response?.status === 403) {
        toast.error("Inventory tracking is only available for product-based companies");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load inventory data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await api.get("/inventory/transactions?limit=50");
      setTransactions(res.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  }, []);

  const fetchValuation = useCallback(async () => {
    try {
      const res = await api.get("/inventory/valuation");
      setValuation(res.data);
    } catch (error) {
      console.error("Error fetching valuation:", error);
    }
  }, []);

  const fetchTurnover = useCallback(async () => {
    try {
      const res = await api.get("/inventory/turnover?months=12");
      setTurnover(res.data);
    } catch (error) {
      console.error("Error fetching turnover:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === "transactions") {
      fetchTransactions();
    } else if (activeTab === "reports") {
      fetchValuation();
      fetchTurnover();
    }
  }, [activeTab, fetchTransactions, fetchValuation, fetchTurnover]);

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleAdjustStock = async () => {
    if (!adjustmentDialog.product) return;
    
    try {
      await api.post("/inventory/adjust", {
        product_id: adjustmentDialog.product.id,
        adjustment_quantity: parseInt(adjustmentForm.adjustment_quantity),
        adjustment_type: adjustmentForm.adjustment_type,
        notes: adjustmentForm.notes || null,
        unit_cost: adjustmentForm.unit_cost ? parseFloat(adjustmentForm.unit_cost) : null
      });
      
      toast.success("Stock adjusted successfully");
      setAdjustmentDialog({ open: false, product: null });
      setAdjustmentForm({ adjustment_quantity: 0, adjustment_type: "adjustment", notes: "", unit_cost: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to adjust stock");
    }
  };

  const handleUpdateProductSettings = async () => {
    if (!productSettingsDialog.product) return;
    
    try {
      await api.put(`/inventory/products/${productSettingsDialog.product.id}`, {
        track_inventory: productSettingsForm.track_inventory,
        stock_quantity: parseInt(productSettingsForm.stock_quantity) || 0,
        reorder_point: parseInt(productSettingsForm.reorder_point) || 0,
        reorder_quantity: parseInt(productSettingsForm.reorder_quantity) || 0,
        max_stock: productSettingsForm.max_stock ? parseInt(productSettingsForm.max_stock) : null,
        location: productSettingsForm.location || null,
        supplier_id: productSettingsForm.supplier_id || null,
        average_cost: productSettingsForm.average_cost ? parseFloat(productSettingsForm.average_cost) : null
      });
      
      toast.success("Product inventory settings updated");
      setProductSettingsDialog({ open: false, product: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    }
  };

  const handleSaveSupplier = async () => {
    try {
      if (supplierDialog.supplier?.id) {
        await api.put(`/suppliers/${supplierDialog.supplier.id}`, supplierForm);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", supplierForm);
        toast.success("Supplier created");
      }
      
      setSupplierDialog({ open: false, supplier: null });
      setSupplierForm({ name: "", contact_name: "", email: "", phone: "", address: "", lead_time_days: 7, payment_terms: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save supplier");
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!confirm("Are you sure you want to delete this supplier?")) return;
    
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success("Supplier deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete supplier");
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await api.patch(`/inventory/alerts/${alertId}/resolve`);
      toast.success("Alert resolved");
      fetchData();
    } catch (error) {
      toast.error("Failed to resolve alert");
    }
  };

  const openProductSettings = (product) => {
    setProductSettingsForm({
      track_inventory: product.track_inventory || false,
      stock_quantity: product.stock_quantity || 0,
      reorder_point: product.reorder_point || 0,
      reorder_quantity: product.reorder_quantity || 0,
      max_stock: product.max_stock || "",
      location: product.location || "",
      supplier_id: product.supplier_id || "",
      average_cost: product.average_cost || ""
    });
    setProductSettingsDialog({ open: true, product });
  };

  const openSupplierDialog = (supplier = null) => {
    if (supplier) {
      setSupplierForm({
        name: supplier.name || "",
        contact_name: supplier.contact_name || "",
        email: supplier.email || "",
        phone: supplier.phone || "",
        address: supplier.address || "",
        lead_time_days: supplier.lead_time_days || 7,
        payment_terms: supplier.payment_terms || "",
        notes: supplier.notes || ""
      });
    } else {
      setSupplierForm({ name: "", contact_name: "", email: "", phone: "", address: "", lead_time_days: 7, payment_terms: "", notes: "" });
    }
    setSupplierDialog({ open: true, supplier });
  };

  const openTransactionHistory = async (product) => {
    try {
      const res = await api.get(`/inventory/transactions/${product.id}`);
      setTransactionHistoryDialog({ open: true, product, transactions: res.data });
    } catch (error) {
      toast.error("Failed to load transaction history");
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.sku && p.sku.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === "all" || p.stock_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="max-w-[1400px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Card key={i}><CardContent className="p-6"><Skeleton className="h-20" /></CardContent></Card>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="inventory-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Package className="h-6 w-6 text-[hsl(var(--ff-primary))]" />
              <div>
                <h1 className="font-semibold text-lg">Inventory Management</h1>
                <p className="text-xs text-muted-foreground">Track stock levels and manage products</p>
              </div>
            </div>
          </div>
          
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-button">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* Alerts Banner */}
        {alerts.length > 0 && (
          <Card className="border-yellow-500/50 bg-yellow-50 dark:bg-yellow-900/10" data-testid="alerts-banner">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <div className="flex-1">
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">
                    {alerts.length} Stock {alerts.length === 1 ? "Alert" : "Alerts"} Requiring Attention
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {alerts.filter(a => a.alert_type === "out_of_stock").length} out of stock, {alerts.filter(a => a.alert_type === "low_stock").length} low stock
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab("dashboard")} className="bg-white dark:bg-transparent">
                  View Alerts
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="suppliers" data-testid="tab-suppliers">Suppliers</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card data-testid="kpi-stock-value">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Total Stock Value</p>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold mt-2">{formatCurrency(overview?.total_stock_value)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {overview?.total_units} units in stock
                  </p>
                </CardContent>
              </Card>
              
              <Card data-testid="kpi-tracking">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Products Tracking</p>
                    <Boxes className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold mt-2">{overview?.products_tracking_inventory}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    of {overview?.total_products} total products
                  </p>
                </CardContent>
              </Card>
              
              <Card data-testid="kpi-low-stock">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Low/Out of Stock</p>
                    <TrendingDown className="h-4 w-4 text-[hsl(var(--ff-expense))]" />
                  </div>
                  <p className="text-2xl font-bold mt-2 text-[hsl(var(--ff-expense))]">
                    {(overview?.low_stock_count || 0) + (overview?.out_of_stock_count || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {overview?.out_of_stock_count} out, {overview?.low_stock_count} low
                  </p>
                </CardContent>
              </Card>
              
              <Card data-testid="kpi-turnover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Turnover Rate</p>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold mt-2">{overview?.average_turnover_rate || 0}x</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Annual inventory turnover
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Products Needing Reorder */}
              <Card data-testid="reorder-list">
                <CardHeader>
                  <CardTitle className="text-lg">Products Needing Reorder</CardTitle>
                  <CardDescription>Items below reorder point or out of stock</CardDescription>
                </CardHeader>
                <CardContent>
                  {overview?.products_needing_reorder?.length > 0 ? (
                    <div className="space-y-3">
                      {overview.products_needing_reorder.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border">
                          <div className="flex-1">
                            <p className="font-medium">{p.name}</p>
                            <p className="text-sm text-muted-foreground">SKU: {p.sku || "N/A"}</p>
                          </div>
                          <div className="text-right">
                            <Badge className={STOCK_STATUS_COLORS[p.status]}>
                              {p.stock_quantity} / {p.reorder_point}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-1">
                              Order: {p.reorder_quantity} units
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="h-10 w-10 mx-auto mb-3 text-green-500" />
                      <p>All products are well stocked!</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Active Alerts */}
              <Card data-testid="alerts-list">
                <CardHeader>
                  <CardTitle className="text-lg">Active Alerts</CardTitle>
                  <CardDescription>Unresolved stock notifications</CardDescription>
                </CardHeader>
                <CardContent>
                  {alerts.length > 0 ? (
                    <div className="space-y-3">
                      {alerts.slice(0, 5).map(alert => (
                        <div key={alert.id} className="flex items-center justify-between p-3 rounded-lg border">
                          <div className="flex items-center gap-3">
                            {alert.alert_type === "out_of_stock" ? (
                              <XCircle className="h-5 w-5 text-red-500" />
                            ) : alert.alert_type === "low_stock" ? (
                              <AlertTriangle className="h-5 w-5 text-yellow-500" />
                            ) : (
                              <AlertCircle className="h-5 w-5 text-blue-500" />
                            )}
                            <div>
                              <p className="font-medium">{alert.product_name}</p>
                              <p className="text-sm text-muted-foreground capitalize">
                                {alert.alert_type.replace("_", " ")} - {alert.current_stock} units
                              </p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleResolveAlert(alert.id)}>
                            Resolve
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="h-10 w-10 mx-auto mb-3 text-green-500" />
                      <p>No active alerts</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Transactions */}
            <Card data-testid="recent-transactions">
              <CardHeader>
                <CardTitle className="text-lg">Recent Transactions</CardTitle>
                <CardDescription>Latest stock movements</CardDescription>
              </CardHeader>
              <CardContent>
                {overview?.recent_transactions?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {overview.recent_transactions.map(tx => (
                        <TableRow key={tx.id}>
                          <TableCell className="text-sm">
                            {tx.created_at ? format(parseISO(tx.created_at), "MMM d, h:mm a") : "-"}
                          </TableCell>
                          <TableCell className="font-medium">{tx.product_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{tx.type}</Badge>
                          </TableCell>
                          <TableCell className={`text-right font-medium ${tx.quantity >= 0 ? "text-green-600" : "text-red-600"}`}>
                            {tx.quantity >= 0 ? "+" : ""}{tx.quantity}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-10 w-10 mx-auto mb-3" />
                    <p>No recent transactions</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search products or SKU..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="product-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ok">In Stock</SelectItem>
                  <SelectItem value="low">Low Stock</SelectItem>
                  <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                  <SelectItem value="overstock">Overstock</SelectItem>
                  <SelectItem value="not_tracking">Not Tracking</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Products Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Stock</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.map(product => (
                      <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{product.name}</p>
                            {product.supplier_name && (
                              <p className="text-xs text-muted-foreground">
                                <Truck className="inline h-3 w-3 mr-1" />
                                {product.supplier_name}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{product.sku || "-"}</TableCell>
                        <TableCell>
                          <Badge className={STOCK_STATUS_COLORS[product.stock_status]}>
                            {STOCK_STATUS_LABELS[product.stock_status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {product.track_inventory ? (
                            <div>
                              <p className="font-medium">{product.stock_quantity}</p>
                              {product.reorder_point > 0 && (
                                <Progress 
                                  value={Math.min((product.stock_quantity / product.reorder_point) * 100, 100)} 
                                  className="h-1 mt-1 w-20 mx-auto"
                                />
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {product.track_inventory ? formatCurrency(product.stock_value) : "-"}
                        </TableCell>
                        <TableCell>
                          {product.location ? (
                            <span className="text-sm">
                              <MapPin className="inline h-3 w-3 mr-1" />
                              {product.location}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`product-menu-${product.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openProductSettings(product)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Inventory Settings
                              </DropdownMenuItem>
                              {product.track_inventory && (
                                <>
                                  <DropdownMenuItem onClick={() => {
                                    setAdjustmentForm({ adjustment_quantity: 0, adjustment_type: "adjustment", notes: "", unit_cost: "" });
                                    setAdjustmentDialog({ open: true, product });
                                  }}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Adjust Stock
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openTransactionHistory(product)}>
                                    <History className="h-4 w-4 mr-2" />
                                    View History
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {filteredProducts.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Package className="h-10 w-10 mx-auto mb-3" />
                    <p>No products found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Transaction History</CardTitle>
                <CardDescription>All stock movements</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">Unit Cost</TableHead>
                        <TableHead>Stock Change</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map(tx => (
                        <TableRow key={tx.id}>
                          <TableCell className="text-sm">
                            {tx.created_at ? format(parseISO(tx.created_at), "MMM d, yyyy h:mm a") : "-"}
                          </TableCell>
                          <TableCell className="font-medium">{tx.product_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{tx.type}</Badge>
                          </TableCell>
                          <TableCell className={`text-right font-medium ${tx.quantity >= 0 ? "text-green-600" : "text-red-600"}`}>
                            {tx.quantity >= 0 ? "+" : ""}{tx.quantity}
                          </TableCell>
                          <TableCell className="text-right">
                            {tx.unit_cost ? formatCurrency(tx.unit_cost) : "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {tx.previous_stock} → {tx.new_stock}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                            {tx.notes || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <History className="h-10 w-10 mx-auto mb-3" />
                    <p>No transactions yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Suppliers Tab */}
          <TabsContent value="suppliers" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => openSupplierDialog()} data-testid="add-supplier-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Supplier
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suppliers.map(supplier => (
                <Card key={supplier.id} data-testid={`supplier-card-${supplier.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold">{supplier.name}</h3>
                        {supplier.contact_name && (
                          <p className="text-sm text-muted-foreground">{supplier.contact_name}</p>
                        )}
                      </div>
                      <Badge variant={supplier.is_active ? "default" : "secondary"}>
                        {supplier.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    
                    <div className="mt-4 space-y-2 text-sm">
                      {supplier.email && (
                        <p className="text-muted-foreground">{supplier.email}</p>
                      )}
                      {supplier.phone && (
                        <p className="text-muted-foreground">{supplier.phone}</p>
                      )}
                      <div className="flex items-center justify-between text-xs">
                        <span>Lead Time: {supplier.lead_time_days} days</span>
                        <span>{supplier.products_count} products</span>
                      </div>
                      {supplier.payment_terms && (
                        <p className="text-xs text-muted-foreground">Terms: {supplier.payment_terms}</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => openSupplierDialog(supplier)}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteSupplier(supplier.id)}>
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {suppliers.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <Truck className="h-10 w-10 mx-auto mb-3" />
                    <p>No suppliers yet</p>
                    <Button variant="outline" className="mt-4" onClick={() => openSupplierDialog()}>
                      Add Your First Supplier
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            {/* Valuation */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Inventory Valuation</CardTitle>
                <CardDescription>Current inventory value breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                {valuation ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-4 bg-muted rounded-lg">
                          <span className="font-medium">Total Inventory Value</span>
                          <span className="text-2xl font-bold">{formatCurrency(valuation.total_value)}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 border rounded-lg">
                            <p className="text-sm text-muted-foreground">Total Units</p>
                            <p className="text-xl font-bold">{valuation.total_units}</p>
                          </div>
                          <div className="p-4 border rounded-lg">
                            <p className="text-sm text-muted-foreground">Avg Unit Value</p>
                            <p className="text-xl font-bold">{formatCurrency(valuation.average_unit_value)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      {valuation.by_location?.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-3">By Location</h4>
                          <div className="h-[200px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={valuation.by_location}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                  nameKey="location"
                                  label={({ location, percent }) => `${location} (${(percent * 100).toFixed(0)}%)`}
                                >
                                  {valuation.by_location.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <Skeleton className="h-[200px]" />
                )}
              </CardContent>
            </Card>

            {/* Turnover */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Inventory Turnover Analysis</CardTitle>
                <CardDescription>How efficiently inventory is sold</CardDescription>
              </CardHeader>
              <CardContent>
                {turnover ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Turnover Rate</p>
                        <p className="text-2xl font-bold">{turnover.turnover_rate}x</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Days to Sell</p>
                        <p className="text-2xl font-bold">{turnover.days_to_sell}</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">COGS (12mo)</p>
                        <p className="text-2xl font-bold">{formatCurrency(turnover.total_cost_of_goods_sold)}</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Avg Inventory</p>
                        <p className="text-2xl font-bold">{formatCurrency(turnover.average_inventory_value)}</p>
                      </div>
                    </div>

                    {turnover.by_product?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3">Top Products by Turnover</h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Product</TableHead>
                              <TableHead className="text-right">Units Sold</TableHead>
                              <TableHead className="text-right">COGS</TableHead>
                              <TableHead className="text-right">Turnover Rate</TableHead>
                              <TableHead className="text-right">Days to Sell</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {turnover.by_product.slice(0, 10).map(p => (
                              <TableRow key={p.product_id}>
                                <TableCell className="font-medium">{p.product_name}</TableCell>
                                <TableCell className="text-right">{p.units_sold}</TableCell>
                                <TableCell className="text-right">{formatCurrency(p.cogs)}</TableCell>
                                <TableCell className="text-right">{p.turnover_rate}x</TableCell>
                                <TableCell className="text-right">{p.days_to_sell} days</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ) : (
                  <Skeleton className="h-[200px]" />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Stock Adjustment Dialog */}
      <Dialog open={adjustmentDialog.open} onOpenChange={(open) => setAdjustmentDialog({ open, product: open ? adjustmentDialog.product : null })}>
        <DialogContent data-testid="adjustment-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
            <DialogDescription>
              Adjust inventory for {adjustmentDialog.product?.name}
              <br />
              Current stock: <strong>{adjustmentDialog.product?.stock_quantity} units</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Adjustment Type</Label>
                <Select 
                  value={adjustmentForm.adjustment_type} 
                  onValueChange={(v) => setAdjustmentForm(f => ({ ...f, adjustment_type: v }))}
                >
                  <SelectTrigger data-testid="adjustment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="adjustment">Manual Adjustment</SelectItem>
                    <SelectItem value="purchase">Purchase/Restock</SelectItem>
                    <SelectItem value="return">Customer Return</SelectItem>
                    <SelectItem value="write_off">Write Off</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Quantity (+/-)</Label>
                <Input
                  type="number"
                  value={adjustmentForm.adjustment_quantity}
                  onChange={(e) => setAdjustmentForm(f => ({ ...f, adjustment_quantity: e.target.value }))}
                  placeholder="e.g., 10 or -5"
                  data-testid="adjustment-quantity"
                />
              </div>
            </div>
            
            {(adjustmentForm.adjustment_type === "purchase" || parseInt(adjustmentForm.adjustment_quantity) > 0) && (
              <div className="space-y-2">
                <Label>Unit Cost (optional)</Label>
                <Input
                  type="number"
                  value={adjustmentForm.unit_cost}
                  onChange={(e) => setAdjustmentForm(f => ({ ...f, unit_cost: e.target.value }))}
                  placeholder="Cost per unit"
                  data-testid="adjustment-unit-cost"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={adjustmentForm.notes}
                onChange={(e) => setAdjustmentForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Reason for adjustment..."
                data-testid="adjustment-notes"
              />
            </div>
            
            {adjustmentForm.adjustment_quantity && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm">
                  New stock: <strong>
                    {(adjustmentDialog.product?.stock_quantity || 0) + parseInt(adjustmentForm.adjustment_quantity || 0)} units
                  </strong>
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustmentDialog({ open: false, product: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleAdjustStock}
              disabled={!adjustmentForm.adjustment_quantity || parseInt(adjustmentForm.adjustment_quantity) === 0}
              data-testid="confirm-adjustment-btn"
            >
              Adjust Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Settings Dialog */}
      <Dialog open={productSettingsDialog.open} onOpenChange={(open) => setProductSettingsDialog({ open, product: open ? productSettingsDialog.product : null })}>
        <DialogContent className="max-w-md" data-testid="product-settings-dialog">
          <DialogHeader>
            <DialogTitle>Inventory Settings</DialogTitle>
            <DialogDescription>{productSettingsDialog.product?.name}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="track-inventory">Track Inventory</Label>
              <Switch
                id="track-inventory"
                checked={productSettingsForm.track_inventory}
                onCheckedChange={(v) => setProductSettingsForm(f => ({ ...f, track_inventory: v }))}
                data-testid="track-inventory-switch"
              />
            </div>
            
            {productSettingsForm.track_inventory && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Current Stock</Label>
                    <Input
                      type="number"
                      value={productSettingsForm.stock_quantity}
                      onChange={(e) => setProductSettingsForm(f => ({ ...f, stock_quantity: e.target.value }))}
                      data-testid="stock-quantity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Average Cost</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={productSettingsForm.average_cost}
                      onChange={(e) => setProductSettingsForm(f => ({ ...f, average_cost: e.target.value }))}
                      placeholder="Unit cost"
                      data-testid="average-cost-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Reorder Point</Label>
                    <Input
                      type="number"
                      value={productSettingsForm.reorder_point}
                      onChange={(e) => setProductSettingsForm(f => ({ ...f, reorder_point: e.target.value }))}
                      placeholder="Alert threshold"
                      data-testid="reorder-point-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reorder Quantity</Label>
                    <Input
                      type="number"
                      value={productSettingsForm.reorder_quantity}
                      onChange={(e) => setProductSettingsForm(f => ({ ...f, reorder_quantity: e.target.value }))}
                      placeholder="Order amount"
                      data-testid="reorder-quantity-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Max Stock (optional)</Label>
                  <Input
                    type="number"
                    value={productSettingsForm.max_stock}
                    onChange={(e) => setProductSettingsForm(f => ({ ...f, max_stock: e.target.value }))}
                    placeholder="Overstock threshold"
                    data-testid="max-stock-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Storage Location</Label>
                  <Input
                    value={productSettingsForm.location}
                    onChange={(e) => setProductSettingsForm(f => ({ ...f, location: e.target.value }))}
                    placeholder="Warehouse, shelf, etc."
                    data-testid="location-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <Select 
                    value={productSettingsForm.supplier_id || "none"} 
                    onValueChange={(v) => setProductSettingsForm(f => ({ ...f, supplier_id: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger data-testid="supplier-select">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Supplier</SelectItem>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductSettingsDialog({ open: false, product: null })}>
              Cancel
            </Button>
            <Button onClick={handleUpdateProductSettings} data-testid="save-settings-btn">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Dialog */}
      <Dialog open={supplierDialog.open} onOpenChange={(open) => setSupplierDialog({ open, supplier: open ? supplierDialog.supplier : null })}>
        <DialogContent data-testid="supplier-dialog">
          <DialogHeader>
            <DialogTitle>{supplierDialog.supplier?.id ? "Edit Supplier" : "Add Supplier"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={supplierForm.name}
                onChange={(e) => setSupplierForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Supplier company name"
                data-testid="supplier-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input
                  value={supplierForm.contact_name}
                  onChange={(e) => setSupplierForm(f => ({ ...f, contact_name: e.target.value }))}
                  placeholder="Primary contact"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={supplierForm.email}
                  onChange={(e) => setSupplierForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="contact@supplier.com"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={supplierForm.phone}
                  onChange={(e) => setSupplierForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div className="space-y-2">
                <Label>Lead Time (days)</Label>
                <Input
                  type="number"
                  value={supplierForm.lead_time_days}
                  onChange={(e) => setSupplierForm(f => ({ ...f, lead_time_days: parseInt(e.target.value) || 7 }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={supplierForm.address}
                onChange={(e) => setSupplierForm(f => ({ ...f, address: e.target.value }))}
                placeholder="Full address"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Payment Terms</Label>
              <Input
                value={supplierForm.payment_terms}
                onChange={(e) => setSupplierForm(f => ({ ...f, payment_terms: e.target.value }))}
                placeholder="e.g., Net 30, COD"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={supplierForm.notes}
                onChange={(e) => setSupplierForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierDialog({ open: false, supplier: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSupplier}
              disabled={!supplierForm.name}
              data-testid="save-supplier-btn"
            >
              {supplierDialog.supplier?.id ? "Update Supplier" : "Add Supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog open={transactionHistoryDialog.open} onOpenChange={(open) => setTransactionHistoryDialog({ open, product: null, transactions: [] })}>
        <DialogContent className="max-w-2xl" data-testid="transaction-history-dialog">
          <DialogHeader>
            <DialogTitle>Transaction History</DialogTitle>
            <DialogDescription>{transactionHistoryDialog.product?.name}</DialogDescription>
          </DialogHeader>
          
          {transactionHistoryDialog.transactions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Stock Change</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactionHistoryDialog.transactions.map(tx => (
                  <TableRow key={tx.id}>
                    <TableCell className="text-sm">
                      {tx.created_at ? format(parseISO(tx.created_at), "MMM d, h:mm a") : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{tx.type}</Badge>
                    </TableCell>
                    <TableCell className={`text-right font-medium ${tx.quantity >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {tx.quantity >= 0 ? "+" : ""}{tx.quantity}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {tx.previous_stock} → {tx.new_stock}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                      {tx.notes || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-10 w-10 mx-auto mb-3" />
              <p>No transaction history for this product</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
